import { useMemo, useState, type ReactNode } from "react";
import { Trans } from "react-i18next";
import { Popover, type PopoverProps } from "antd";
import { useControllableValue } from "ahooks";
import classNames from "classnames";
import { IconLineExport } from "@kalodata/kds-design-icons";
import { CommonButton } from "@components/CommonButton";
import libraryService from "@apis/libraryService";
import { TestId } from "@shared/testIds";
import { GeneratedDetailExportMenuContent, type ExportMenuDownloadProps } from "./GeneratedDetailExportMenuContent";
import { PostToTikTokModal } from "./PostToTikTokModal/PostToTikTokModal";

export type GeneratedDetailExportMenuProps = {
  disabled?: boolean;
  className?: string;
  assetId: number;
  download: ExportMenuDownloadProps;
  renderTrigger?: (ctx: { disabled?: boolean; open: boolean }) => ReactNode;
  popoverTrigger?: PopoverProps["trigger"];
  popoverPlacement?: PopoverProps["placement"];
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onPostModalOpenChange?: (open: boolean) => void;
};

export const GeneratedDetailExportMenu = (props: GeneratedDetailExportMenuProps) => {
  const {
    assetId,
    disabled,
    className,
    download,
    renderTrigger,
    popoverTrigger = "click",
    popoverPlacement = "top",
    onPostModalOpenChange,
  } = props;
  const [open, setOpen] = useControllableValue<boolean>(props, {
    defaultValue: false,
    valuePropName: "open",
    trigger: "onOpenChange",
  });
  const [postModalOpen, setPostModalOpen] = useState(false);

  const handlePostModalOpenChange = (nextOpen: boolean) => {
    setPostModalOpen(nextOpen);
    onPostModalOpenChange?.(nextOpen);
  };

  const downloadProps = useMemo<ExportMenuDownloadProps>(
    () => ({
      ...download,
      onDownload: download.onDownload ?? (() => libraryService.downloadReport({ assetId })),
    }),
    [download, assetId],
  );

  const trigger = renderTrigger ? (
    renderTrigger({ disabled, open })
  ) : (
    <div className={classNames("w-full", className)}>
      <CommonButton
        type="primary"
        className="w-full"
        disabled={disabled}
        data-testid={TestId.LibraryGeneratedVideoDetailExport}
      >
        <IconLineExport size={20} className="shrink-0" />
        <Trans>Export</Trans>
      </CommonButton>
    </div>
  );

  return (
    <>
      <Popover
        open={disabled ? false : open}
        onOpenChange={(next) => {
          if (!disabled) setOpen(next);
        }}
        trigger={popoverTrigger}
        placement={popoverPlacement}
        arrow={false}
        destroyOnHidden
        content={
          <GeneratedDetailExportMenuContent
            download={downloadProps}
            onClose={() => setOpen(false)}
            onOpenPostModal={() => handlePostModalOpenChange(true)}
          />
        }
        styles={{ container: { padding: 0, borderRadius: 12 } }}
      >
        {trigger}
      </Popover>
      <PostToTikTokModal assetId={assetId} open={postModalOpen} onClose={() => handlePostModalOpenChange(false)} />
    </>
  );
};
